import { useQuery, type UseQueryOptions } from "@tanstack/react-query";
import { apiFetch, ApiFetchError } from "../lib/api";
import { queryKeys } from "./keys";
import type {
  ConversationGroupView,
  Conversation,
  DashboardSnapshot,
  Message,
  User,
} from "../../server/types";

// ────────────────────────────────────────────────────────────────────────
// Read hooks.
//
// Each hook names its exact query key via `queryKeys` and takes an
// `enabled` option when the caller sometimes shouldn't fetch (e.g. the
// chat page without a conversation id). Caller-supplied options spread
// last so a page can override `staleTime` or `refetchOnWindowFocus`
// without re-implementing the fetch.
// ────────────────────────────────────────────────────────────────────────

type MeResponse = { user: User };

/** Resolve the signed-in user, or `null` if the session cookie is absent.
 *  401s are treated as "not signed in" rather than errors so the route
 *  guard can render Login without a flash. */
export function useMe(
  options?: Partial<UseQueryOptions<User | null, ApiFetchError>>,
) {
  return useQuery<User | null, ApiFetchError>({
    queryKey: queryKeys.auth.me,
    queryFn: async ({ signal }) => {
      try {
        const res = await apiFetch<MeResponse>("/api/auth/me", { signal });
        return res.user;
      } catch (err) {
        if (err instanceof ApiFetchError && err.status === 401) return null;
        throw err;
      }
    },
    // Session state is cheap to re-check but shouldn't thrash. Five
    // minutes mirrors a typical cookie refresh window.
    staleTime: 5 * 60 * 1000,
    retry: false,
    ...options,
  });
}

export function useConversations(
  search?: string,
  options?: Partial<
    UseQueryOptions<ConversationGroupView[], ApiFetchError>
  >,
) {
  return useQuery<ConversationGroupView[], ApiFetchError>({
    queryKey: queryKeys.conversations.list(search),
    queryFn: async ({ signal }) => {
      const url = search
        ? `/api/conversations?q=${encodeURIComponent(search)}`
        : "/api/conversations";
      const res = await apiFetch<{ groups: ConversationGroupView[] }>(url, {
        signal,
      });
      return res.groups;
    },
    // Keep the previous list visible while a new search fetches so the
    // sidebar doesn't flash to empty between keystrokes.
    keepPreviousData: true,
    ...options,
  });
}

type ConversationDetail = { conversation: Conversation; messages: Message[] };

export function useConversation(
  id: string | null | undefined,
  options?: Partial<UseQueryOptions<ConversationDetail, ApiFetchError>>,
) {
  return useQuery<ConversationDetail, ApiFetchError>({
    queryKey: id ? queryKeys.conversations.detail(id) : ["conversations", "detail", "__none__"],
    queryFn: ({ signal }) =>
      apiFetch<ConversationDetail>(`/api/conversations/${id}`, { signal }),
    enabled: Boolean(id),
    ...options,
  });
}

export function useDashboardSnapshot(
  options?: Partial<UseQueryOptions<DashboardSnapshot, ApiFetchError>>,
) {
  return useQuery<DashboardSnapshot, ApiFetchError>({
    queryKey: queryKeys.dashboard.snapshot,
    queryFn: ({ signal }) =>
      apiFetch<DashboardSnapshot>("/api/dashboard", { signal }),
    ...options,
  });
}
